/**
 * Preset screens — the builder's *Start from a preset* step (owner, 2026-10-29;
 * plan: `docs/plans/movers.md` step 8, mock: `docs/movers-mock.html`).
 *
 * **A preset is copied, never linked**, the condition presets' rule: picking one
 * fills the builder's draft — its conditions (copied into the library and
 * ticked), its columns, its sort and its name — and from then on the screen is
 * the member's own. A later change here never reaches a screen made from it.
 *
 * Gated by `screen:preset:<id>` (Live, st-jas, st-jp) and **hidden** without it,
 * never shown locked (owner, 2026-10-29). Columns are the plan's 17 where the
 * screen has the field; three are left for later and named in the plan — Float
 * (`freeFloat` is a per-symbol depth metric and a percent, not a screener
 * column), the Earnings BMO/AMC chip, and Last trade.
 */

import type { ScreenerColumn, ScreenerFormatRule, ScreenerSort } from "@qufolio/model";

import { ALL_PRESETS, type ConditionPreset, type PresetGate } from "./condition-presets.js";

export interface ScreenPreset {
  /** The preset's key, and the tail of its capability (`screen:preset:<id>`). */
  id: string;
  name: string;
  /** One line under the name: the session and what it sorts by. */
  blurb: string;
  requires: string;
  /** Condition presets copied in and ticked, by `ConditionPreset.id`. */
  conditions: readonly string[];
  columns: readonly ScreenerColumn[];
  sort: ScreenerSort;
}

const SIGN: ScreenerFormatRule[] = [
  { when: { op: "gte", value: 0 }, style: { fg: "positive" } },
  { when: { op: "lt", value: 0 }, style: { fg: "negative" } },
];

/** Green / red by the sign of another column on the row — Last by its %Chg. */
const signOf = (field: string): ScreenerFormatRule[] => [
  { when: { field, op: "gte", value: 0 }, style: { fg: "positive" } },
  { when: { field, op: "lt", value: 0 }, style: { fg: "negative" } },
];

/**
 * RVol: ≥ 2× bold, ≥ 5× highlighted (the plan's format). Later rules merge.
 * `scale` 100 for the standard `rvol`, which reads 150 for 1.5×.
 */
const rvolFormat = (scale: number): ScreenerFormatRule[] => [
  { when: { op: "gte", value: 2 * scale }, style: { bold: true } },
  { when: { op: "gte", value: 5 * scale }, style: { bg: "selection", bold: true } },
];

interface Session {
  last: string;
  pct: string;
  chg: string;
  volume: string;
  rvol: string;
  /** 1 for Pre/Post RVol (1.5 = 1.5×), 100 for the standard `rvol` (150). */
  rvolScale: number;
  high: string;
  low: string;
  news: string;
}

function columns(s: Session, base: "prevClose" | "close"): ScreenerColumn[] {
  return [
    { field: "logo" },
    { field: "symbol", header: "Symbol", pinned: "left", width: 90 },
    { field: "name", header: "Name", width: 180 },
    base === "close"
      ? { field: "close", header: "Close", precision: 2, width: 80 }
      : { field: "prevClose", header: "Prev", precision: 2, width: 80 },
    { field: s.last, header: "Last", precision: 2, width: 80, format: signOf(s.pct) },
    { field: s.pct, header: "%Chg", precision: 2, width: 80, format: SIGN },
    { field: s.chg, header: "Chg", precision: 2, width: 80, format: SIGN },
    { field: s.volume, header: "Volume", width: 100 },
    { field: s.rvol, header: "RVol", precision: s.rvolScale === 1 ? 1 : 0, width: 70, format: rvolFormat(s.rvolScale) },
    {
      field: `${s.volume}Dollar`,
      header: "$Vol",
      width: 100,
      computed: {
        expression: {
          kind: "binary",
          op: "*",
          left: { kind: "field", field: s.last },
          right: { kind: "field", field: s.volume },
        },
        source: `${s.last} * ${s.volume}`,
        rollup: "recompute",
      },
    },
    { field: s.high, header: "High", precision: 2, width: 80 },
    { field: s.low, header: "Low", precision: 2, width: 80 },
    { field: "marketCap", header: "Mkt Cap", width: 100 },
    { field: "sector", header: "Sector", width: 140 },
    { field: "nextEarningsDate", header: "Earnings", width: 100 },
    { field: s.news, header: "News", width: 320 },
  ];
}

export const SCREEN_PRESETS: readonly ScreenPreset[] = [
  {
    id: "pre-movers",
    name: "Pre Movers",
    blurb: "04:00–09:30 ET · Pre Mover · Pre % ↓",
    requires: "screen:preset:pre-movers",
    conditions: ["preMover"],
    columns: columns(
      { last: "preClose", pct: "prePct", chg: "preChange", volume: "preVolume", rvol: "preRvol", rvolScale: 1,
        high: "preHigh", low: "preLow", news: "preNews" },
      "prevClose",
    ),
    sort: { field: "prePct", dir: "desc" },
  },
  {
    id: "post-movers",
    name: "Post Movers",
    blurb: "16:00–20:00 ET · Post Mover · Post % ↓",
    requires: "screen:preset:post-movers",
    conditions: ["postMover"],
    columns: columns(
      { last: "postClose", pct: "postPct", chg: "postChange", volume: "postVolume", rvol: "postRvol", rvolScale: 1,
        high: "postHigh", low: "postLow", news: "postNews" },
      "close",
    ),
    sort: { field: "postPct", dir: "desc" },
  },
  {
    id: "market-movers",
    name: "Market Movers",
    blurb: "09:30–16:00 ET · Market Mover · % ↓",
    requires: "screen:preset:market-movers",
    conditions: ["marketMover"],
    columns: columns(
      { last: "close", pct: "changePercentCc", chg: "changeCc", volume: "volume", rvol: "rvol", rvolScale: 100,
        high: "high", low: "low", news: "news" },
      "prevClose",
    ),
    sort: { field: "changePercentCc", dir: "desc" },
  },
];

/** The screens `allowed` offers. None when the host passes no gate. */
export function offeredScreenPresets(allowed?: PresetGate): ScreenPreset[] {
  return SCREEN_PRESETS.filter((p) => allowed?.(p.requires) ?? false);
}

/** A screen preset's conditions, as the condition presets they name. */
export function screenPresetConditions(preset: ScreenPreset): ConditionPreset[] {
  return preset.conditions
    .map((id) => ALL_PRESETS.find((c) => c.id === id))
    .filter((c): c is ConditionPreset => c !== undefined);
}
